import React from 'react';
import styles from '../styles/Kanban.module.css';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { formatDate } from '../utils/dateFormat';
import { useLocalization } from '../context/LocalizationContext';

const KanbanColumn = ({ column, tasks, onTaskClick, onQuickStatusChange, canViewFinancials, canEditTasks, collapsedView }) => {
  const { t } = useLocalization();
  const formatTaskDate = (dateValue) => formatDate(dateValue);
  const formatCurrency = (value) => `RM ${Number(value || 0).toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const priorityLabel = (priority) => {
    const map = {
      low: t('tasks_priority_low', 'Low'),
      medium: t('tasks_priority_medium', 'Medium'),
      high: t('tasks_priority_high', 'High'),
      urgent: t('tasks_priority_urgent', 'Urgent'),
    };
    return map[priority] || priority;
  };
  const parseMemberNames = (value) => {
    if (!value) return [];
    return String(value)
      .split(', ')
      .map((item) => item.trim())
      .filter(Boolean);
  };
  const parseResourceSummary = (value) => {
    if (!value) return [];
    return String(value)
      .split('||')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const truncateDescription = (text, maxLength = 100) => {
    if (!text) return '';
    const lines = String(text).split('\n');
    const firstLine = lines[0] || '';
    if (firstLine.length > maxLength) {
      return firstLine.substring(0, maxLength) + '...';
    }
    return firstLine;
  };

  return (
    <div className={styles.column}>
      <div className={styles.columnHeader}>
        <h3>{column.title}</h3>
        <span className={styles.taskCount}>{tasks.length}</span>
      </div>
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`${styles.taskList} ${snapshot.isDraggingOver ? styles.draggingOver : ''}`}
          >
            {tasks.map((task, index) => (
              <Draggable key={task.id} draggableId={String(task.id)} index={index}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`${styles.taskCard} ${snapshot.isDragging ? styles.dragging : ''}`}
                    onClick={() => onTaskClick(task)}
                  >
                    {collapsedView ? (
                      // Collapsed view - only title, date, priority, and first line of description
                      <div className={styles.taskCardCollapsed}>
                        <div className={styles.taskHeader}>
                          <div className={styles.titleWrap}>
                            <h4>{task.title}</h4>
                            <span className={styles.dateHighlight}>{formatTaskDate(task.task_date)}</span>
                          </div>
                          <span className={`${styles.priority} ${styles[task.priority]}`}>
                            {priorityLabel(task.priority)}
                          </span>
                        </div>
                        <p className={styles.taskDescShort}>{truncateDescription(task.description)}</p>
                        {task.status === 'in_progress' && canEditTasks && (
                          <div className={styles.taskActionButtons}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onQuickStatusChange(task, 'completed');
                              }}
                              className={`${styles.quickStatusBtn} ${styles.completedBtn}`}
                              title={t('tasks_mark_completed', 'Mark Completed')}
                            >
                              <i className="bi bi-check-circle" aria-hidden="true"></i> {t('tasks_completed', 'Completed')}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onQuickStatusChange(task, 'cancelled');
                              }}
                              className={`${styles.quickStatusBtn} ${styles.cancelBtn}`}
                              title={t('tasks_mark_cancelled', 'Cancel Task')}
                            >
                              <i className="bi bi-x-circle" aria-hidden="true"></i> {t('tasks_cancel', 'Cancel')}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Expanded view - full card
                      <div className={styles.taskCardExpanded}>
                        <div className={styles.taskHeader}>
                          <div className={styles.titleWrap}>
                            <h4>{task.title}</h4>
                            <span className={styles.dateHighlight}>{formatTaskDate(task.task_date)}</span>
                          </div>
                          <span className={`${styles.priority} ${styles[task.priority]}`}>
                            {priorityLabel(task.priority)}
                          </span>
                        </div>
                        <p className={styles.taskDesc}>{task.description}</p>
                        <div className={styles.taskInfoGrid}>
                          <div className={styles.taskInfoColumn}>
                            <div className={styles.metaSection}>
                              <div className={styles.metaTitle}>{t('kanban_team', 'Team')}</div>
                              <div className={styles.teamName}>{task.team_name || t('tasks_unassigned', 'Unassigned')}</div>
                            </div>
                            <div className={styles.metaSection}>
                              <div className={styles.metaTitle}>{t('kanban_members', 'Members')}</div>
                              {parseMemberNames(task.member_names).length > 0 ? (
                                <div className={styles.memberList}>
                                  {parseMemberNames(task.member_names).map((name, i) => (
                                    <span key={i} className={styles.memberBadge}>
                                      <i className="bi bi-person-fill" aria-hidden="true"></i> {name}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <div className={styles.metaEmpty}>{t('kanban_no_members', 'No members assigned')}</div>
                              )}
                            </div>
                          </div>
                          <div className={`${styles.taskInfoColumn} ${styles.financialColumn}`}>
                            <div className={styles.metaSection}>
                              <div className={styles.metaTitle}>{t('kanban_resources', 'Resources')}</div>
                              {parseResourceSummary(task.resource_summary).length > 0 ? (
                                <div className={styles.resourceList}>
                                  {parseResourceSummary(task.resource_summary).map((resource, i) => (
                                    <span key={`${task.id}-resource-${i}`} className={styles.resourceBadge}>
                                      <i className="bi bi-box-seam" aria-hidden="true"></i> {resource}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <div className={styles.metaEmpty}>{t('kanban_no_resources', 'No resources assigned')}</div>
                              )}
                            </div>
                          {canViewFinancials && (
                            <>
                              <div className={styles.metaSection}>
                                <div className={styles.metaTitle}>{t('kanban_financials', 'Financials')}</div>
                                <div className={styles.financialList}>
                                  <div className={styles.financialRow}>
                                    <span className={styles.financialLabel}>{t('kanban_financial_resource_cost', 'Resource Cost')}</span>
                                    <span className={`${styles.financialValue} ${styles.costText}`}>{formatCurrency(task.resource_cost)}</span>
                                  </div>
                                  <div className={styles.financialRow}>
                                    <span className={styles.financialLabel}>{t('kanban_financial_user_cost', 'User Cost')}</span>
                                    <span className={`${styles.financialValue} ${styles.costText}`}>{formatCurrency(task.user_cost)}</span>
                                  </div>
                                  <div className={styles.financialRow}>
                                    <span className={styles.financialLabel}>{t('kanban_financial_revenue', 'Revenue')}</span>
                                    <span className={`${styles.financialValue} ${styles.revenueText}`}>{formatCurrency(task.revenue_amount)}</span>
                                  </div>
                                  <div className={styles.financialRow}>
                                    <span className={styles.financialLabel}>{t('kanban_financial_profit', 'Profit')}</span>
                                    <span className={`${styles.financialValue} ${Number(task.profit_amount || 0) >= 0 ? styles.profitTextPositive : styles.profitTextNegative}`}>
                                      {formatCurrency(task.profit_amount)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default KanbanColumn;
